import * as R from 'ramda';
import { getErrorShortCircuit, keyFor, getLoading } from '../../../../core/redux/api/module';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { Entitlement } from '../../models';
import ImmutableMap from '../../../../language/ImmutableMap';
import { IAppState } from '../../../../core/redux/IAppState';
import { Resort } from '../../../../resortDetails/resort';


/**
 * The Resort Abbreviation for all resorts
 */
export const ALL_RESORTS = 'SHOWALL';


/**
 * This stores the information used for the aggregate All Resorts stat summary.
 */
export type AllResortStats = {
  totalDays: number,
  resorts: number,
  mostVisited: {
    name: string,
    stays: number
  },
};


/**
 * Retrieves the resort name based on the provided resortAbbrev from the provided resorts
 */
export const getResortName = (
  resortAbbrev: string,
  resorts: ImmutableMap<Resort>,
): string => resortAbbrev === ALL_RESORTS ? 'All Resorts' : R.path(['resortName'], resorts.get(resortAbbrev)) || '';


/**
 * This calculates the stats displayed when the user has selected the All Resorts aggregate from the dropdown.
 */
export const getAggregateStats = (history: Entitlement): AllResortStats => {
  const stayCounts = history.redemptionHistory.reduce(
    (running: ImmutableMap<number>, next) => running.add(
      next.redeemResort,
      ((running.get(next.redeemResort) || 0) + next.redeemQuantity),
    ),
    ImmutableMap.empty(),
  );

  const resortsVisited = stayCounts.count();

  const mostVisited = stayCounts.reduce(
    (running, nextKey, nextValue: number) => nextValue > running.stays ? { name: nextKey, stays: nextValue } : running,
    { name: '', stays: 0 },
  );

  return {
    totalDays: history.used,
    resorts:   resortsVisited,
    mostVisited,
  };
};


/**
 * Gets the loading resort history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest. Need to provide a resort abbreviation since multiple can be queued.
 */
export const getLoadingResortHistory = (state: IAppState, requestId: string, resortAbbrev: string) => {
  const resort = resortAbbrev || 'SHOWALL';

  return state.loading.any((key, value) => key === keyFor(ACTION_TYPE.RESORT_HISTORY_REQUEST, requestId)
                                           && value.extras.resortAbbrev === resort);
};


/**
 * Gets the error resort history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest.
 */
export const getErroredResortHistory = getErrorShortCircuit(ACTION_TYPE.RESORT_HISTORY_REQUEST);


/**
 * Gets the loading of redemption history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest.
 */
export const getLoadingRedemptionHistory = (state: IAppState, requestId: string) => 
  getLoading(state, requestId, ACTION_TYPE.REDEMPTION_HISTORY_REQUEST);


/**
 * Gets the error for a redemption history for the given Guest ID if it exists, and if the guest ID isn't undefined.
 * Perfect for if you don't know if you even have a guest.
 */
export const getErroredRedemptionHistory = getErrorShortCircuit(ACTION_TYPE.REDEMPTION_HISTORY_REQUEST);
